import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MessageSquare, Send, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const API = `${BACKEND_URL}/api`;

const ROLES = ["Founder / CEO", "Sales Lead", "Operations Manager", "Customer Support Lead", "Other"];

const inputStyle = {
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "13px",
  fontWeight: 300,
  letterSpacing: "0.01em",
  width: "100%",
  padding: "12px 16px",
  background: "rgba(0,0,0,0.4)",
  border: "1px solid rgba(255,255,255,0.08)",
  color: "#fff",
  outline: "none",
  transition: "border-color 0.25s",
  borderRadius: "0",
};

const labelStyle = {
  fontFamily: "'Montserrat', sans-serif",
  fontSize: "10px",
  letterSpacing: "0.15em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.5)",
  marginBottom: "8px",
  display: "block",
};

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        phone: form.phone.trim() || null,
        role: form.role || null,
      });
      toast.success("Thanks — we'll be in touch within 24 hours.");
      setForm({ name: "", email: "", phone: "", role: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      style={{
        position: "relative",
        padding: "96px 0 144px",
        background: "rgba(10,10,10,0.70)",
        overflow: "hidden",
      }}
    >
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "1px", background: "linear-gradient(90deg, transparent, rgba(210,170,60,0.15), transparent)" }} />

      <div style={{ position: "relative", maxWidth: "1280px", margin: "0 auto", padding: "0 24px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "64px" }}>
          {/* Left - content */}
          <div>
            <div
              className="reveal"
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "10px",
                letterSpacing: "0.4em",
                color: "rgba(210,170,60,0.8)",
                textTransform: "uppercase",
                marginBottom: "20px",
              }}
            >
              Contact
            </div>
            <h2
              className="reveal reveal-delay-1"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "clamp(36px, 5vw, 60px)",
                fontWeight: 300,
                lineHeight: 1.08,
                color: "#fff",
                letterSpacing: "0.02em",
                marginBottom: "24px",
              }}
            >
              Not ready to book? <br />
              <em style={{ color: "#e8c45a", fontStyle: "italic", fontWeight: 400 }}>Say hello first.</em>
            </h2>
            <p
              className="reveal reveal-delay-2"
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "14px",
                fontWeight: 300,
                color: "rgba(220,195,140,0.6)",
                lineHeight: 1.8,
                letterSpacing: "0.02em",
                marginBottom: "40px",
              }}
            >
              Send us a note about your market, your offer, and where you feel stuck.
              We'll reply within 24 hours with a thoughtful response — no auto-responders.
            </p>

            {/* Contact info */}
            <div className="reveal reveal-delay-3" style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    border: "1px solid rgba(210,170,60,0.2)",
                    background: "rgba(210,170,60,0.04)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Mail style={{ width: "16px", height: "16px", color: "#e8c45a" }} />
                </div>
                <div>
                  <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "12px", fontWeight: 400, color: "#fff", marginBottom: "4px", letterSpacing: "0.05em" }}>Direct Email</div>
                  <a
                    href="mailto:management@tmnresourcesolutions.com"
                    style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "13px", fontWeight: 300, color: "rgba(255,255,255,0.5)", textDecoration: "none", transition: "color 0.25s" }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#e8c45a")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.5)")}
                  >
                    management@tmnresourcesolutions.com
                  </a>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    border: "1px solid rgba(210,170,60,0.2)",
                    background: "rgba(210,170,60,0.04)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <MessageSquare style={{ width: "16px", height: "16px", color: "#e8c45a" }} />
                </div>
                <div>
                  <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "12px", fontWeight: 400, color: "#fff", marginBottom: "4px", letterSpacing: "0.05em" }}>Response Time</div>
                  <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "13px", fontWeight: 300, color: "rgba(255,255,255,0.5)" }}>Within 24 business hours</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - form */}
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="reveal reveal-delay-2"
            style={{
              padding: "40px 36px",
              background: "rgba(8,8,8,0.6)",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "20px" }}>
              <div>
                <label htmlFor="contact-name" style={labelStyle}>Full Name *</label>
                <input
                  id="contact-name"
                  data-testid="contact-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Jane Doe"
                  style={inputStyle}
                  onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(210,170,60,0.4)")}
                  onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)")}
                />
              </div>
              <div>
                <label htmlFor="contact-email" style={labelStyle}>Email *</label>
                <input
                  id="contact-email"
                  type="email"
                  data-testid="contact-email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@company.com"
                  style={inputStyle}
                  onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(210,170,60,0.4)")}
                  onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)")}
                />
              </div>
              <div>
                <label htmlFor="contact-phone" style={labelStyle}>Phone</label>
                <input
                  id="contact-phone"
                  type="tel"
                  data-testid="contact-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="(555) 555-0100"
                  style={inputStyle}
                  onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(210,170,60,0.4)")}
                  onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)")}
                />
              </div>
              <div>
                <label htmlFor="contact-role" style={labelStyle}>I am a...</label>
                <select
                  id="contact-role"
                  data-testid="contact-role"
                  value={form.role}
                  onChange={update("role")}
                  style={{ ...inputStyle, appearance: "none", cursor: "pointer" }}
                >
                  <option value="">Select a role</option>
                  {ROLES.map((r) => (
                    <option key={r} value={r} style={{ background: "#080808" }}>{r}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginTop: "20px" }}>
              <label htmlFor="contact-message" style={labelStyle}>Message *</label>
              <textarea
                id="contact-message"
                data-testid="contact-message"
                value={form.message}
                onChange={update("message")}
                rows={5}
                placeholder="Tell us about your business, which services you need..."
                style={{ ...inputStyle, resize: "vertical", minHeight: "120px" }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(210,170,60,0.4)")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)")}
              />
            </div>

            <button
              type="submit"
              data-testid="contact-submit"
              disabled={submitting}
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "11px",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                fontWeight: 400,
                marginTop: "24px",
                padding: "14px 36px",
                background: "rgba(210,165,40,0.15)",
                border: "1px solid rgba(210,165,40,0.7)",
                color: "#e8c45a",
                cursor: submitting ? "not-allowed" : "pointer",
                transition: "all 0.35s",
                borderRadius: "0",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                opacity: submitting ? 0.6 : 1,
              }}
            >
              {submitting ? (
                "Sending..."
              ) : (
                <>
                  <Send style={{ width: "14px", height: "14px" }} />
                  Send Message
                  <ArrowRight style={{ width: "14px", height: "14px" }} />
                </>
              )}
            </button>

            <p style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "10px", color: "rgba(255,255,255,0.3)", marginTop: "16px", letterSpacing: "0.05em" }}>
              We never share your details. Submissions stored securely.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
